import {
  observable,
  action,
  autorun,
  computed
} from 'mobx'
import { setter }        from 'mobx-decorators'
import xhr               from 'helpers/XHR'
import moment            from 'moment'
import { padCharsStart } from 'lodash/fp'
import uiStore           from 'stores/UiStore'
import userStore         from 'stores/UserStore'
import getError          from 'helpers/ErrorParser'
import _                 from 'lodash'
import { Device }        from '@twilio/voice-sdk'
import { postEvent }     from 'helpers/Segment'

const EXPIRED_CODE = 31205

export default class CallingStore {
  @setter @observable callBarVisible = false
  @setter @observable callTime = null
  @setter @observable isCalling = false
  @setter @observable isCellCalling = false
  @setter @observable isCellSelected = false
  @setter @observable showCallDialog = false
  @setter @observable isCallNotesSelected = false
  @setter @observable isDialPadSelected = false
  @setter @observable isMuteSelected = false
  @setter @observable isError = null
  @setter @observable isSaved = null
  @setter @observable showIncoming = false

  @observable twilioToken = null
  @observable connection = null
  @observable contact = null
  @observable currentOutputDevice = null
  @observable isConnected = false
  @observable outputDevices = []
  @observable phoneNumber = null
  @observable studentID = null
  @observable callSID = null
  @observable callNoteText = ''

  @observable callStartTime = null
  @observable intervalHandler = null

  // Device instance property
  device = null

  constructor() {
    this.initAutoruns()
    this.setupCalling()
  }

  setupDevice = () => {
    if (!this.twilioToken) {
      console.warn('Cannot setup device: No token available')
      return
    }

    try {
      // Create new Device instance with correct options
      this.device = new Device(this.twilioToken, {
        // Remove edge parameter as it's not required in this format in 2.x
        debug: true,
        // Add proper codecs
        codecPreferences: ['opus', 'pcmu'],
        // Add proper ICE servers setup
        allowIncomingWhileBusy: true
      })

      console.log('Twilio device created successfully')

      // Setup device listeners after device is created
      if (this.device) {
        this.setupDeviceListeners()
      }
    } catch (err) {
      console.error('Error creating Twilio device:', err)
      this.setIsError({
        message: `Failed to initialize calling device: ${err.message}`,
        title: 'Device Error'
      })
    }
  }

  setupDeviceListeners = () => {
    if (!this.device) return

    console.log('Setting up device listeners')

    // Add detailed error listener with expanded logging
    this.device.on(
      'error',
      action((error) => {
        console.error('Device error:', error)

        if (error.code !== EXPIRED_CODE) {
          const errorMessage = returnError(error.code)
          console.error(`Error code ${error.code}: ${errorMessage}`)

          this.setIsError({
            message: errorMessage,
            title:   'Error'
          })

          postEvent('web:calling:web_call_error', {
            message: error.message,
            code:    error.code
          })
        }
      })
    )

    // Add state change listener for debugging
    this.device.on('registered', () => {
      console.log('Device registered successfully')
    })

    this.device.on('unregistered', () => {
      console.log('Device unregistered')
    })

    // Add incoming call listener with more debugging
    this.device.on('incoming', (conn) => {
      console.log('Incoming call received:', conn)
      this.handleConnection(conn)
      this.setShowIncoming(true)

      if (conn.parameters && conn.parameters.From) {
        console.log('Incoming call from:', conn.parameters.From)
        this.fetchIncomingInfo(conn.parameters.From)
      } else {
        console.warn('Incoming call missing From parameter')
      }
    })

    // Register for incoming calls
    console.log('Attempting to register device for incoming calls')
    this.device.register()
      .then(() => {
        console.log('Device registered successfully')
      })
      .catch(err => {
        console.error('Failed to register device', err)
        this.setIsError({
          message: `Failed to register for incoming calls: ${err.message}`,
          title: "Registration Error"
        })
      })
  }

  setupCalling = async() => {
    try {
      await this.generateToken()
      if (this.twilioToken) {
        this.setupDevice()
        this.setupTokenRefresh()
      }
    } catch (error) {
      console.error('Error setting up calling:', error)
      this.setIsError(getError(error))
    }
  }

  /* Computed */

  @computed
  get contactName() {
    if (!this.contact) return null

    return this.contact.name
  }

  @computed
  get contactId() {
    if (!this.contact) return null

    return this.contact.id
  }

  @computed
  get contactRelationship() {
    if (!this.contact) return null

    return this.contact.relationship
  }

  @computed
  get contactStudent() {
    if (!this.contact) return null

    return this.contact.student
  }

  /* Autoruns */

  @action
  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  @action
  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    this.isError.type || 'error'
        })
      }
    })
  }

  /* Actions */

  @action
  generateToken = async() => {
    try {
      console.log('Requesting new token from server')
      const {
        data: { token }
      } = await xhr.get('/commo/capability_token')

      console.log('Received token from server')
      this.twilioToken = token
      return token
    } catch (e) {
      console.error('Failed to generate token:', e)
      this.setIsError(getError(e))
      throw e
    }
  }

  @action
  setupTokenRefresh = () => {
    if (!this.device) return
    // Listen for token expiration
    this.device.on('tokenWillExpire', async () => {
      try {
        const token = await this.generateToken()
        if (token && this.device) {
          this.device.updateToken(token)
        }
      } catch (error) {
        console.error('Failed to refresh token:', error)
      }
    })
  }

  @action
  answerIncoming = () => {
    if (this.connection) {
      this.connection.accept()
      this.setShowIncoming(false)
    }
  }

  @action
  rejectIncoming = () => {
    if (this.connection) {
      this.connection.reject()
      this.setShowIncoming(false)
    }
  }

  @action
  handleIncomingAccept = () => {
    this.setCallBarVisible(true)
    this.setIsCalling(true)
    this.isConnected = true
    this.callStartTime = Date.now()
  }

  @action
  fetchIncomingInfo = async(id) => {
    try {
      const { data } = await xhr.get(
        `/contacts/${cleanIncomingId(id)}`
      )

      this.contact = data
    } catch (e) {
      this.setIsError(e)
    }
  }

  @action
  resetCallBar = () => {
    this.setIsCalling(true)
    this.callStartTime = null
    this.setShowCallDialog(false)
    this.setCallTime(null)
    this.setCallBarVisible(true)
    this.setIsCellSelected(false)
    this.setIsDialPadSelected(false)
  }

  @action
  initiateCall = async(studentId) => {
    try {
      if (this.isCalling && this.connection) {
        this.hangUp()
      }

      if (!this.twilioToken) {
        await this.generateToken()
        if (this.twilioToken && !this.device) {
          this.setupDevice()
        }
      }

      this.resetCallBar()
      this.initiateCallOK(studentId)
    } catch (e) {
      this.setIsError(getError(e))
    }
  }

  @action
  initiateCallOK = (studentId) => {
    this.studentID = studentId
    this.phoneNumber = this.contact.phone

    setTimeout(() => this.connect(this.phoneNumber), 2000)
  }

  @action
  connect = async () => {
    if (!this.device) {
      this.setIsError({
        message: "Device not initialized",
        title: "Error"
      })
      return
    }

    const { id: userID } = userStore
    const params = {
      contact_id: this.contact ? this.contact.id : null,
      student_id: this.studentID,
      tocall:     this.phoneNumber,
      user_id:    userID
    }

    postEvent('web:calling:web', params)

    try {
      console.log('Attempting to connect call with params:', params)

      // Connect returns a Promise in 2.x - use proper options format
      const call = await this.device.connect({
        params: params,
        // Add audio constraints for better connection handling
        audioConstraints: true
      })

      console.log('Call connected successfully:', call)
      this.handleConnection(call)
    } catch (error) {
      console.error('Connection error:', error)
      this.setIsError({
        message: error.message || "Failed to connect call",
        title: "Connection Error"
      })
    }
  }

  @action
  handleConnection = (conn) => {
    if (!conn) {
      console.error('Received null connection object')
      return
    }

    console.log('Handling connection:', conn)
    this.connection = conn

    // Add detailed error listener first
    conn.on('error', (error) => {
      console.error('Call error:', error)
      this.setIsError({
        message: `Call error: ${error.message || 'Unknown error'}`,
        title: 'Call Error'
      })
    })

    // Use event listeners instead of direct assignment
    conn.on('accept', (call) => {
      console.log('Call accepted:', call)
      this.handleConnectionAccept(call)
    })

    conn.on('disconnect', (call) => {
      console.log('Call disconnected:', call)
      this.handleConnectionEnd()
    })

    conn.on('reject', () => {
      console.log('Call rejected')
      this.handleConnectionEnd()
    })

    conn.on('cancel', () => {
      console.log('Call canceled')
      this.handleConnectionEnd()
    })

    // For incoming calls
    if (!this.isCalling && conn.direction === 'INCOMING') {
      this.setShowIncoming(true)
    }
  }

  @action
  handleConnectionAccept = (conn) => {
    this.callSID = conn.parameters.CallSid
    this.getOutputDevices()
    this.initCallTimer()
    this.isConnected = true
    this.setCallBarVisible(true)
    this.setIsCalling(true)
  }

  @action
  handleConnectionEnd = () => {
    this.isConnected = false
    this.setShowIncoming(false)
    this.setIsCalling(false)
    clearInterval(this.intervalHandler)
    setTimeout(
      action(() => !this.isCalling && this.setCallBarVisible(false)),
      5000
    )
  }

  // Dialpad
  @action
  sendDigit = (digit) => {
    if (this.connection) {
      this.connection.sendDigits(digit)
    }
  }

  // Cell-to-Cell Calling
  @action
  initiateCellCall = async(contact, student_id) => {
    try {
      if (!this.twilioToken) {
        await this.generateToken()
        if (this.twilioToken && !this.device) {
          this.setupDevice()
        }
      }

      this.initiateCellCallOK(contact, student_id)
    } catch (e) {
      this.setIsError(getError(e))
    }
  }

  @action
  initiateCellCallOK = (contact, student_id) => {
    const { id: userID } = userStore

    this.contactID = contact.id
    this.phoneNumber = contact.phone
    this.studentID = student_id
    this.userId = userID

    this.completeCellCall()
  }

  @action
  completeCellCall = async() => {
    postEvent('web:calling:cell', { contact: this.contactID })

    try {
      await xhr.post('/commo/voice/mobile_call', {
        contact_id: this.contactID
      })

      this.completeCellCallOK()
    } catch (e) {
      this.setIsError(getError(e))
      postEvent('web:calling:cell_call_error', {
        message: e.message
      })
    }
  }

  @action
  completeCellCallOK = () => {
    this.setIsCellCalling(true)
    this.setCallBarVisible(true)
    this.setShowCallDialog(false)

    setTimeout(
      action(() => {
        this.setIsCellCalling(false)
        !this.isCalling && this.setCallBarVisible(false)
      }),
      6000
    )
  }

  @action
  getOutputDevices = () => {
    if (!this.device || !this.device.audio || !this.device.audio.availableOutputDevices) {
      console.warn('No device or audio devices available')
      return
    }

    // Clear previous devices
    this.outputDevices = []

    // Note: the forEach parameters in the new API are (id, device) instead of (device, id)
    this.device.audio.availableOutputDevices.forEach((id, device) => {
      this.outputDevices.push({ label: device.label, id })
    })
  }

  @action
  changeOutput = (device, label) => {
    if (!this.device || !this.device.audio || !this.device.audio.speakerDevices) {
      console.warn('No device or audio devices available')
      return
    }

    this.device.audio.speakerDevices.set(new Set([device]))
    this.currentOutputDevice = label
  }

  @action
  hangUp = () => {
    if (this.connection) {
      this.connection.disconnect()
    }
  }

  @action
  initCallTimer = () => {
    this.setCallTime(null)
    this.callStartTime = Date.now()

    this.intervalHandler = setInterval(
      action('SETTING CALL TIME', () => {
        this.setCallTime(getTime(this.callStartTime, Date.now()))
      }),
      1000
    )
  }

  @action
  toggleMute = () => {
    if (!this.connection) return

    this.setIsMuteSelected(!this.isMuteSelected)

    this.connection.mute(this.isMuteSelected)
  }

  /* Call Notes */
  @action
  setCallNoteText = (text) => {
    this.callNoteText = text
  }

  @action
  addCallNote = async() => {
    try {
      this.setIsError(false)

      if (_.isEmpty(this.callNoteText.trim())) {
        this.setIsError({
          title:   'Note',
          message: 'Your note cannot be empty!',
          type:    'error'
        })
        return
      }

      await xhr.post('/commo/call_log/deferred_note', {
        call_log_sid: this.callSID,
        body:         this.callNoteText
      })

      this.addCallNoteOK()
    } catch (e) {
      this.setIsError(getError(e))
    }
  }

  @action
  addCallNoteOK = () => {
    this.setCallNoteText('')

    uiStore.addNotification({
      title:   'Note',
      message: 'Note successfully added!',
      type:    'success'
    })
  }

  @action
  cancelCallSelection = () => {
    this.setIsCellSelected(false)
    this.setShowCallDialog(false)
  }
}

function getTime(start, end) {
  const startTime = moment(start)
  const endTime = moment(end)
  const duration = moment.duration(endTime.diff(startTime))
  const padTime = padCharsStart('0')(2)

  const hours = parseInt(duration.asHours()).toString()
  const minutes = (
    parseInt(duration.asMinutes()) -
    hours * 60
  ).toString()
  const secs = (
    parseInt(duration.asSeconds()) -
    minutes * 60
  ).toString()

  return `${padTime(minutes)}:${padTime(secs)}`
}

function returnError(errorCode) {
  switch (errorCode) {
  case 31000:
    return `We can't complete this call because it is either
            blocked by a firewall or you have lost connection to the Internet.`
  case 31002:
    return `This contact's number does not allow communication from SchoolStatus.`
  case 31003:
    return `We can't complete this call because it is either
            blocked by a firewall or you have lost connection to the Internet.`
  case 31208:
    return 'We require access to your microphone.'
  case 31201:
    return `We can't access your microphone. Please check your permissions.`
  default:
    return 'We experienced an unknown error.'
  }
}

function cleanIncomingId(id) {
  try {
    const incomingContact = JSON.parse(
      atob(id.replace('client:', '').replace(/_/g, '='))
    )
    return incomingContact.contact_id
  } catch (e) {
    console.error('Error parsing incoming ID:', e)
    return null
  }
}
